import { useEffect, useRef, useState } from 'react';

export type ClipboardState = 'idle' | 'copied' | 'failed';

const DefaultClipboardResetDelay = 2000 as const;

type ClipboardSource = string | (() => string | Promise<string>);

interface Clipboard {
	state: ClipboardState;
	copy: (source: ClipboardSource) => Promise<void>;
}

export function useClipboard(
	resetDelay: number = DefaultClipboardResetDelay
): Clipboard {
	const [state, setState] = useState<ClipboardState>('idle');
	const pending = useRef(false);

	useEffect(() => {
		if (state === 'idle') {
			return;
		}

		const timer = setTimeout(() => setState('idle'), resetDelay);

		return () => clearTimeout(timer);
	}, [state, resetDelay]);

	async function copy(source: ClipboardSource): Promise<void> {
		if (pending.current) {
			return;
		}

		pending.current = true;

		try {
			const text = typeof source === 'function' ? await source() : source;

			await navigator.clipboard.writeText(text);

			setState('copied');
		} catch {
			setState('failed');
		} finally {
			pending.current = false;
		}
	}

	return { state, copy };
}
